<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const isUpdating = ref(false);
const showTakeoverConfirm = ref(false);

const setting = computed(() =>
  store.getters['conversationBot/getConversationBotSetting'](
    props.conversationId
  )
);
const personas = computed(() => store.getters['botPersonas/getPersonas']);

const botEnabled = computed(() => setting.value?.bot_enabled ?? false);
const agentTakeover = computed(() => setting.value?.agent_takeover ?? false);
const activePersona = computed(
  () => personas.value.find(p => p.id === setting.value?.bot_persona_id) || null
);

async function load() {
  isLoading.value = true;
  await Promise.all([
    store.dispatch(
      'conversationBot/fetchConversationSetting',
      props.conversationId
    ),
    store.dispatch('botPersonas/fetch'),
  ]);
  isLoading.value = false;
}

async function toggleBot() {
  isUpdating.value = true;
  await store.dispatch('conversationBot/updateConversationSetting', {
    conversationId: props.conversationId,
    bot_enabled: !botEnabled.value,
  });
  useAlert(t('MINISTRY.BOT_PANEL.TOGGLE_SUCCESS'));
  isUpdating.value = false;
}

async function setPersona(personaId) {
  await store.dispatch('conversationBot/updateConversationSetting', {
    conversationId: props.conversationId,
    bot_persona_id: personaId || null,
  });
}

async function confirmTakeover() {
  showTakeoverConfirm.value = false;
  isUpdating.value = true;
  await store.dispatch('conversationBot/updateConversationSetting', {
    conversationId: props.conversationId,
    agent_takeover: true,
    bot_enabled: false,
  });
  useAlert(t('MINISTRY.BOT_PANEL.TAKEOVER_SUCCESS'));
  isUpdating.value = false;
}

watch(() => props.conversationId, load);
onMounted(load);
</script>

<template>
  <div v-if="isLoading" class="flex justify-center py-4">
    <span class="i-lucide-loader-2 size-4 animate-spin text-n-slate-8" />
  </div>

  <div v-else class="flex flex-col gap-3">
    <div class="rounded-xl border border-n-weak overflow-hidden">
      <div
        class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak"
        :class="botEnabled ? 'bg-woot-500/10' : 'bg-n-slate-2'"
      >
        <span
          class="i-lucide-bot size-4"
          :class="botEnabled ? 'text-woot-500' : 'text-n-slate-6'"
        />
        <div class="flex-1 min-w-0">
          <p class="text-xs font-semibold text-n-slate-12">
            {{
              botEnabled
                ? t('MINISTRY.BOT_PANEL.BOT_ACTIVE')
                : t('MINISTRY.BOT_PANEL.BOT_OFF')
            }}
          </p>
          <p v-if="activePersona" class="text-[10px] text-n-slate-8 truncate">
            {{ activePersona.name }}
          </p>
        </div>
        <button
          :disabled="isUpdating || agentTakeover"
          class="text-[10px] px-2 py-1 rounded-md border transition-colors disabled:opacity-50"
          :class="
            botEnabled
              ? 'border-amber-300 text-amber-600 hover:bg-amber-50'
              : 'border-green-300 text-green-600 hover:bg-green-50'
          "
          @click="toggleBot"
        >
          {{
            botEnabled
              ? t('MINISTRY.BOT_PANEL.DISABLE')
              : t('MINISTRY.BOT_PANEL.ENABLE')
          }}
        </button>
      </div>

      <div class="px-3 py-2.5 flex flex-col gap-2">
        <div class="flex flex-col gap-1">
          <p class="text-[10px] text-n-slate-8 font-medium">
            {{ t('MINISTRY.BOT_PANEL.OVERRIDE_PERSONA') }}
          </p>
          <select
            :value="setting?.bot_persona_id || ''"
            :disabled="!botEnabled || agentTakeover"
            class="w-full rounded-lg border border-n-weak px-2 py-1.5 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-400 disabled:opacity-50"
            @change="e => setPersona(e.target.value || null)"
          >
            <option value="">
              {{ t('MINISTRY.BOT_PANEL.NO_PERSONA') }}
            </option>
            <option
              v-for="persona in personas"
              :key="persona.id"
              :value="persona.id"
            >
              {{ persona.name }}
            </option>
          </select>
        </div>

        <div v-if="!agentTakeover" class="flex justify-end">
          <button
            :disabled="isUpdating"
            class="text-[10px] px-2 py-1 rounded-md border border-red-300 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
            @click="showTakeoverConfirm = true"
          >
            <span class="i-lucide-user size-3 mr-0.5" />
            {{ t('MINISTRY.BOT_PANEL.AGENT_TAKEOVER') }}
          </button>
        </div>

        <div
          v-else
          class="flex items-center gap-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-[10px] text-red-600"
        >
          <span class="i-lucide-user-check size-3" />
          {{ t('MINISTRY.BOT_PANEL.AGENT_TAKEOVER') }}
        </div>
      </div>
    </div>

    <div
      v-if="showTakeoverConfirm"
      class="rounded-xl border border-red-200 bg-red-50 p-3 flex flex-col gap-2"
    >
      <p class="text-xs text-red-700">
        {{ t('MINISTRY.BOT_PANEL.TAKEOVER_CONFIRM') }}
      </p>
      <div class="flex gap-2">
        <button
          class="flex-1 text-xs px-2 py-1.5 rounded-md border border-n-weak text-n-slate-8 hover:bg-white transition-colors"
          @click="showTakeoverConfirm = false"
        >
          {{ t('MINISTRY.BOT_PERSONAS.CANCEL') }}
        </button>
        <button
          class="flex-1 text-xs px-2 py-1.5 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors"
          @click="confirmTakeover"
        >
          {{ t('MINISTRY.BOT_PANEL.AGENT_TAKEOVER') }}
        </button>
      </div>
    </div>
  </div>
</template>
